import React, { useRef, useEffect, useState, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Box, Typography, Grid, Card, CardContent, Chip, IconButton, Tooltip } from '@mui/material';
import { CenterFocusStrong as CenterIcon } from '@mui/icons-material';
import { reverseGeocodeShortCached } from '../../utils/geocoding';

// You'll need to set this token - get it from https://account.mapbox.com/access-tokens/
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1IjoiZGVtby1hY2NvdW50IiwiYSI6ImNrOHV0Zm9qMTAwZmEzb3BnbHNpbGIyZXEifQ.VLdlIGh1EWfVV7jz2v7QyA';

const MapboxFleetMap = ({ 
  trucks = [], 
  height = '400px', 
  onTruckClick = null
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markers = useRef([]);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [truckAddresses, setTruckAddresses] = useState({});
  const [hasInitiallyFitBounds, setHasInitiallyFitBounds] = useState(false);
  const [mapInitError, setMapInitError] = useState(false);

  const validTrucks = useMemo(
    () => (Array.isArray(trucks) ? trucks : []).filter((truck) => truck.latitude && truck.longitude),
    [trucks]
  );

  const resetMapView = () => {
    if (!map.current || validTrucks.length === 0) return;
    
    if (validTrucks.length === 1) {
      const truck = validTrucks[0];
      map.current.flyTo({
        center: [truck.longitude, truck.latitude],
        zoom: 16,
        duration: 1000
      });
    } else {
      const bounds = new mapboxgl.LngLatBounds();
      validTrucks.forEach(truck => {
        bounds.extend([truck.longitude, truck.latitude]);
      });
      map.current.fitBounds(bounds, {
        padding: 50,
        maxZoom: 15,
        duration: 1000
      });
    }
  };

  const destroyMap = () => {
    markers.current.forEach((marker) => {
      try { marker.remove(); } catch (e) { /* already gone */ }
    });
    markers.current = [];
    if (map.current) {
      try { map.current.remove(); } catch (e) { /* already gone */ }
      map.current = null;
    }
  };

  useEffect(() => {
    if (!mapContainer.current || validTrucks.length === 0) {
      destroyMap();
      return undefined;
    }

    if (map.current) {
      setTimeout(() => {
        try {
          if (map.current && map.current.isStyleLoaded()) {
            map.current.resize();
          }
        } catch (err) {
          console.error('MapboxFleetMap resize failed:', err);
          destroyMap();
        }
      }, 100);
      return undefined;
    }

    // Check if Mapbox token is available
    if (!mapboxgl.accessToken || mapboxgl.accessToken.includes('demo')) {
      // Show fallback message if no token
      mapContainer.current.innerHTML = `
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          background: #f5f5f5;
          text-align: center;
          padding: 20px;
          color: #666;
        ">
          <div style="font-size: 48px; margin-bottom: 16px;">🗺️</div>
          <h3 style="margin: 0 0 8px 0; color: #333;">Fleet Map Ready</h3>
          <p style="margin: 0 0 16px 0;">Add your Mapbox token to see ${validTrucks.length} trucks on the map</p>
          <div style="background: #e3f2fd; padding: 12px; border-radius: 8px; font-size: 14px;">
            <strong>Setup Instructions:</strong><br/>
            1. Get token from mapbox.com<br/>
            2. Add to Heroku: heroku config:set REACT_APP_MAPBOX_TOKEN="your_token"
          </div>
        </div>
      `;
      return undefined;
    }

    const lats = validTrucks.map(truck => truck.latitude);
    const lngs = validTrucks.map(truck => truck.longitude);
    
    const centerLat = lats.reduce((sum, lat) => sum + lat, 0) / lats.length;
    const centerLng = lngs.reduce((sum, lng) => sum + lng, 0) / lngs.length;

    try {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [centerLng, centerLat],
        zoom: 11
      });
      setMapInitError(false);
    } catch (err) {
      console.error('MapboxFleetMap failed to initialize:', err);
      setMapInitError(true);
      return undefined;
    }

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    map.current.on('load', () => {
      setTimeout(() => {
        try {
          if (map.current && map.current.isStyleLoaded()) {
            map.current.resize();
          }
        } catch (err) {
          console.error('MapboxFleetMap load resize failed:', err);
        }
      }, 200);
    });

    setTimeout(() => {
      try {
        if (map.current && map.current.isStyleLoaded()) {
          map.current.resize();
        }
      } catch (err) {
        console.error('MapboxFleetMap delayed resize failed:', err);
      }
    }, 500);

    return () => {
      destroyMap();
    };
  }, [validTrucks.length]);

  // Handle resize when container size changes
  useEffect(() => {
    const handleResize = () => {
      if (map.current) {
        setTimeout(() => {
          try {
            if (map.current) map.current.resize();
          } catch (err) {
            console.error('MapboxFleetMap window resize failed:', err);
          }
        }, 100);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch addresses for trucks - use trucks as dependency to avoid infinite loop
  useEffect(() => {
    const fetchAddresses = async () => {
      const trucksWithGPS = trucks.filter(truck => truck.latitude && truck.longitude);
      
      if (trucksWithGPS.length === 0) return;

      const addressPromises = trucksWithGPS.map(async (truck) => {
        try {
          const address = await reverseGeocodeShortCached(truck.latitude, truck.longitude);
          return { truckId: truck.truck_id, address };
        } catch (error) {
          return { truckId: truck.truck_id, address: 'Address unavailable' };
        }
      });

      const addressResults = await Promise.all(addressPromises);
      const addressMap = {};
      addressResults.forEach(({ truckId, address }) => {
        addressMap[truckId] = address;
      });
      setTruckAddresses(addressMap);
    };

    fetchAddresses();
  }, [trucks]);

  // Update markers when trucks change
  useEffect(() => {
    if (!map.current) return;

    try {
      markers.current.forEach((marker) => marker.remove());
    } catch (err) {
      console.error('MapboxFleetMap marker clear failed:', err);
    }
    markers.current = [];

    try {

    validTrucks.forEach((truck) => {
      const getTruckStatus = (truck) => {
        if (!truck.ignition_status || truck.ignition_status === 'Off') return 'offline';
        
        if (truck.ignition_status === 'On') {
          if (truck.speed_kph > 5) {
            return 'driving';
          } else if (truck.job_in_progress) {
            return 'working';
          } else {
            return 'idle';
          }
        }
        return 'parked';
      };

      const status = getTruckStatus(truck);
      
      const getStatusColor = () => {
        switch (status) {
          case 'driving': return '#2196F3'; // Blue
          case 'working': return '#FF9800'; // Orange  
          case 'idle': return '#FFC107'; // Yellow
          case 'parked': return '#4CAF50'; // Green
          default: return '#757575'; // Gray
        }
      };

      // Create custom marker element
      const el = document.createElement('div');
      el.className = 'truck-marker';
      el.style.cssText = `
        background-color: ${getStatusColor()};
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
      `;

      // Add truck emoji directly in the marker
      const iconEl = document.createElement('div');
      iconEl.textContent = truck.speed_kph > 5 ? '🚚' : '🚛';
      iconEl.style.cssText = `
        font-size: 14px;
        line-height: 1;
        pointer-events: none;
        filter: drop-shadow(1px 1px 1px rgba(0,0,0,0.3));
      `;
      el.appendChild(iconEl);

      // Create marker with proper anchoring
      const marker = new mapboxgl.Marker({
        element: el,
        anchor: 'center' // This ensures proper positioning
      })
        .setLngLat([truck.longitude, truck.latitude])
        .addTo(map.current);

      // Add click handler
      el.addEventListener('click', () => {
        setSelectedTruck(truck);
        if (onTruckClick) {
          onTruckClick(truck);
        }
      });

      // Create popup attached to marker
      const popup = new mapboxgl.Popup({ 
        closeOnClick: false,
        anchor: 'top',
        offset: [0, 10],
        className: 'truck-popup'
      })
        .setHTML(`
          <div style="text-align: center; padding: 8px; min-width: 180px;">
            <strong>${truck.truck_name}</strong><br/>
            <span style="color: ${getStatusColor()};">${status.toUpperCase()}</span> • ${Math.round(truck.speed_kph || 0)} km/h<br/>
            <div style="margin: 6px 0; padding: 4px; background: #f5f5f5; border-radius: 4px; font-size: 12px;">
              📍 <span id="address-${truck.truck_id}">Loading address...</span>
            </div>
            ${truck.driver_first_name ? `👤 ${truck.driver_first_name} ${truck.driver_last_name}<br/>` : ''}
            ${truck.current_job_id ? `🔧 Job: ${truck.job_number}` : '📅 No active job'}
          </div>
        `);

      // Attach popup to marker for proper coordinate tracking
      marker.setPopup(popup);
      
      // Update popup address when it opens (simpler approach)
      popup.on('open', () => {
        const addressElement = document.getElementById(`address-${truck.truck_id}`);
        if (addressElement) {
          const address = truckAddresses[truck.truck_id];
          if (address) {
            addressElement.textContent = address;
          }
        }
      });
      
      // Add hover effects without interfering with positioning
      let hoverTimeout;
      el.addEventListener('mouseenter', () => {
        // Use box-shadow and filter for hover effect instead of transform
        el.style.boxShadow = '0 4px 12px rgba(0,0,0,0.4), 0 0 0 2px rgba(33, 150, 243, 0.3)';
        el.style.filter = 'brightness(1.1)';
        el.style.transition = 'box-shadow 0.2s ease, filter 0.2s ease';
        clearTimeout(hoverTimeout);
        hoverTimeout = setTimeout(() => {
          if (marker.getPopup()) {
            marker.getPopup().addTo(map.current);
          }
        }, 100);
      });
      
      el.addEventListener('mouseleave', () => {
        // Reset to original styling
        el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
        el.style.filter = 'none';
        clearTimeout(hoverTimeout);
        hoverTimeout = setTimeout(() => {
          if (marker.getPopup()) {
            marker.getPopup().remove();
          }
        }, 150);
      });

      markers.current.push(marker);
    });

    // Fit map to show all trucks only on initial load, not on every refresh
    if (validTrucks.length > 1 && !hasInitiallyFitBounds) {
      const bounds = new mapboxgl.LngLatBounds();
      validTrucks.forEach(truck => {
        bounds.extend([truck.longitude, truck.latitude]);
      });
      map.current.fitBounds(bounds, {
        padding: 50,
        maxZoom: 15
      });
      setHasInitiallyFitBounds(true); // Prevent future auto-fits
    }
    } catch (err) {
      console.error('MapboxFleetMap marker update failed:', err);
    }

  }, [validTrucks, onTruckClick]);

  if (validTrucks.length === 0) {
    return (
      <Box
        style={{
          height,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px'
        }}
      >
        <Typography color="textSecondary">
          No GPS data available for fleet tracking
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height, display: 'flex', flexDirection: 'row' }}>  
      {/* Map Section - Takes most of the space */}
      <Box
        sx={{
          flex: 1,
          height: '100%',
          minHeight: { xs: '300px', md: '400px' },
          position: 'relative'
        }}
      >
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
          {mapInitError ? (
            <Box
              role="img"
              aria-label="Fleet map unavailable"
              sx={{
                width: '100%',
                height: '100%',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#f5f5f5',
                color: 'text.secondary',
                px: 2,
                textAlign: 'center'
              }}
            >
              <Typography>Fleet map unavailable. Truck list is still shown.</Typography>
            </Box>
          ) : (
            <Box
              ref={mapContainer}
              className="mapbox-container"
              data-testid="mapbox-fleet-container"
              sx={{
                width: '100%',
                height: '100%',
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid #e0e0e0',
                position: 'relative'
              }}
            />
          )}
          
          {/* Reset View Button */}
          <Tooltip title="Center on all trucks">
            <IconButton
              onClick={resetMapView}
              aria-label="Center on all trucks"
              sx={{
                position: 'absolute',
                top: 12,
                right: 52, // Position to the left of Mapbox controls
                backgroundColor: 'white',
                boxShadow: 2,
                '&:hover': {
                  backgroundColor: 'grey.100'
                },
                zIndex: 1000
              }}
            >
              <CenterIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      {/* Truck List Sidebar - Fixed width */}
      <Box sx={{ 
        width: { xs: '100%', md: '280px' },
        height: '100%',
        maxHeight: { xs: '300px', md: '100%' }, 
        overflow: 'auto',
        pl: 1,
        borderLeft: { xs: 'none', md: '1px solid #e0e0e0' },
        display: { xs: validTrucks.length > 0 ? 'block' : 'none', md: 'block' }
      }}>
        <Typography variant="h6" gutterBottom>
          Fleet ({validTrucks.length})
        </Typography>
        
        {validTrucks.map((truck) => {
          const getTruckStatus = (truck) => {
            if (!truck.ignition_status || truck.ignition_status === 'Off') return 'offline';
            
            if (truck.ignition_status === 'On') {
              if (truck.speed_kph > 5) {
                return 'driving';
              } else if (truck.job_in_progress) {
                return 'working';
              } else {
                return 'idle';
              }
            }
            return 'parked';
          };

          const status = getTruckStatus(truck);
          
          const getStatusColor = (status) => {
            switch (status) {
              case 'driving': return 'info';
              case 'working': return 'warning';  
              case 'idle': return 'default';
              case 'parked': return 'success';
              default: return 'error';
            }
          };

          const getStatusText = (status) => {
            switch (status) {
              case 'driving': return 'Driving';
              case 'working': return 'On Site';
              case 'idle': return 'Idle';
              case 'parked': return 'Parked';
              default: return 'Offline';
            }
          };
          
          return (
            <Card
              key={truck.truck_id}
              sx={{
                mb: 1,
                cursor: 'pointer',
                backgroundColor: selectedTruck?.truck_id === truck.truck_id ? 'action.selected' : 'background.paper',
                '&:hover': { boxShadow: 3 }
              }}
              onClick={() => {
                setSelectedTruck(truck);
                if (map.current) {
                  map.current.flyTo({
                    center: [truck.longitude, truck.latitude],
                    zoom: 16,
                    duration: 1000
                  });
                }
                if (onTruckClick) {
                  onTruckClick(truck);
                }
              }}
            >
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {truck.truck_name}
                  </Typography>
                  <Chip
                    label={getStatusText(status)}
                    color={getStatusColor(status)}
                    size="small"
                  />
                </Box>
                
                <Typography variant="caption" display="block" color="textSecondary">
                  {Math.round(truck.speed_kph || 0)} km/h
                </Typography>

                {truck.driver_first_name && (
                  <Typography variant="caption" display="block" color="textSecondary">
                    {truck.driver_first_name} {truck.driver_last_name}
                  </Typography>
                )}

                {truck.current_job_id && (
                  <Typography variant="caption" display="block" color="primary">
                    {truck.job_number}
                  </Typography>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default MapboxFleetMap;
